import React, { PureComponent } from 'react'
import cx from 'classnames'
import { sentry } from 'common/helpers'

import { randomString, supportsInlineSVG, uniquifySVGIDs } from './utils'

import s from './InlineSvg.scss'


const Status = {
  PENDING: 'pending',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
  UNSUPPORTED: 'unsupported',
} as const

type StatusType = ValueOf<typeof Status>

const getRequestsByUrl = {}
const loadedFiles = {}


type InlineSvgProps = {
  className?: string
  preloader?: React.ReactNode
  src: string
  cache?: boolean
  noWrapper?: boolean
  uniquifyIDs?: boolean
  uniqueHash?: string
  tag?: string
  baseURL?: string
  processSVG?: (svg: string) => string
}

type InlineSvgState = {
  status: StatusType
  loadedText?: string
}


class InlineSvg extends PureComponent<InlineSvgProps, InlineSvgState> {

  static defaultProps = {
    cache: false,
    uniquifyIDs: true,
    tag: 'div',
  }

  isActive = false

  constructor(props, context) {
    super(props, context)

    this.state = {
      status: Status.PENDING,
      loadedText: null,
    }
  }

  componentDidMount() {
    const { src } = this.props
    const { status } = this.state

    this.isActive = true

    if (status === Status.PENDING) {
      if (supportsInlineSVG()) {
        if (src) {
          this.startLoad()
          return
        }

        this.fail('InlineSVG: Empty src')
        return
      }

      this.fail('InlineSVG: Unsupported browser')
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { src } = this.props

    if (prevProps.src !== src) {
      if (src) {
        this.startLoad()
        return
      }

      this.fail('Missing source')
    }
  }

  componentWillUnmount() {
    this.isActive = false
  }

  fail(message) {
    console.warn(message)

    if (this.isActive) {
      this.setState({
        status: Status.FAILED,
      })
    }
  }

  startLoad() {
    if (this.isActive) {
      this.setState({
        status: Status.LOADING,
      }, this.load)
    }
  }

  request(src, cb) {
    const xhr = new XMLHttpRequest()

    xhr.onload = () => {
      if (xhr.status !== 200) {
        cb(`InlineSvg: Request failed: ${src}, status: ${xhr.status}`)
      }
      else if (xhr.response === null) {
        cb(`InlineSvg: Empty response: ${src}`)
      }
      else {
        cb(null, xhr.response)
      }
    }

    xhr.onerror = () => {
      cb(`InlineSvg: Request failed: ${src}`)
    }

    xhr.open('GET', src, true)
    xhr.responseType = 'text'
    xhr.send()
  }

  load() {
    const { src, cache } = this.props

    if (cache) {
      if (loadedFiles[src]) {
        const [ err, res ] = loadedFiles[src]

        this.handleLoad(err, res)
        return
      }

      if (!getRequestsByUrl[src]) {
        getRequestsByUrl[src] = [ this.handleLoad ]

        this.request(src, (err, res) => {
          getRequestsByUrl[src].forEach((cb) => {
            const { src: currentSrc } = this.props
            loadedFiles[src] = [ err, res ]

            if (src === currentSrc) {
              cb(err, res)
            }
          })

          delete getRequestsByUrl[src]
        })
      }
      else {
        getRequestsByUrl[src].push(this.handleLoad)
      }

      return
    }

    this.request(src, (err, res) => {
      const { src: currentSrc } = this.props

      if (currentSrc === src) {
        this.handleLoad(err, res)
      }
    })
  }

  handleLoad = (err, res) => {
    if (err) {
      this.fail(err)
      return
    }

    if (this.isActive) {
      this.setState({
        loadedText: res,
        status: Status.LOADED,
      })
    }
  }

  processSVG(text) {
    const {
      uniquifyIDs,
      uniqueHash,
      baseURL,
      processSVG,
    } = this.props

    let svgText = text
    if (processSVG) {
      svgText = processSVG(svgText)
    }

    // remove xml
    svgText = svgText.replace(/<\?xml(.*?)\?>/, '')

    if (uniquifyIDs) {
      return uniquifySVGIDs(svgText, uniqueHash || randomString(), baseURL)
    }

    return svgText
  }

  renderPreloader() {
    const { status } = this.state
    const { children, className, tag, preloader } = this.props

    let content = preloader

    if (status === Status.UNSUPPORTED || status === Status.FAILED) {
      content = children // fallback - use <img /> in children
    }

    return React.createElement(tag, { className: cx(s.svg, className) }, content)
  }

  renderSvg() {
    const { loadedText } = this.state
    const { className, tag } = this.props

    const html = this.processSVG(loadedText)

    return React.createElement(tag, {
      className: cx(s.svg, className),
      draggable: false,
      dangerouslySetInnerHTML: html ? { __html: html } : null,
    })
  }

  renderSvgWithoutWrapper() {
    const { loadedText } = this.state
    const { className } = this.props

    const html = this.processSVG(loadedText)

    if (!html) {
      return null
    }

    try {
      const svgTag = html.match(/<svg[^>]+>/)[0].replace(/[\n\t]/g, ' ')
      const body = html.replace('</svg>', '')
        .replace(/<svg[^>]+>/, '')
        .trim()

      const svgAttrs = svgTag.match(/\w+="[\w\s]+"/g)
        .reduce((obj, item) => {
          const [ key, value ] = item.split('=')

          return {
            ...obj,
            [key.trim()]: value.replace(/"/g, '')
              .trim(),
          }
        }, {})

      return React.createElement('svg', {
        className: cx(s.svg, className),
        draggable: false,
        ...svgAttrs,
        dangerouslySetInnerHTML: { __html: body },
      })
    }
    catch (err) {
      sentry.captureException(err, { extra: this.props })

      return null
    }
  }

  render() {
    const { loadedText } = this.state
    const { noWrapper } = this.props

    if (!loadedText) {
      return this.renderPreloader()
    }

    if (noWrapper) {
      return this.renderSvgWithoutWrapper()
    }

    return this.renderSvg()
  }
}


export default InlineSvg
